import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

/**
 * Wire types for the catalog-hotkeys admin surface. The canonical
 * shape lives in `packages/contracts/schemas/domain/catalog-hotkeys.ts`;
 * this file mirrors what the BOS serialiser returns (BigInt versions
 * already coerced to JS Number on the wire).
 */

export type CatalogHotkeyNodeKind = 'group' | 'item' | 'function';

export type HotkeyNode = {
  id: string;
  parentId: string | null;
  sortOrder: number;
  label: string;
  kind: CatalogHotkeyNodeKind;
  payload: string | null;
  /** RFL-206: catalog category bound to a group node, or null. */
  categoryId: string | null;
  iconName: string | null;
  version: number;
  createdAt: string;
  updatedAt: string;
};

export type HotkeyTreeResponse = {
  version: number;
  nodes: HotkeyNode[];
  snapshotAt: string;
};

export type FunctionKeyEntry = {
  key: string;
  label: string;
  category: 'discount' | 'basket' | 'system';
};

export type FunctionKeyVocabulary = {
  keys: FunctionKeyEntry[];
  snapshotAt: string;
};

const TREE_KEY = ['catalog-hotkeys', 'tree'] as const;
const FN_KEY = ['catalog-hotkeys', 'function-keys'] as const;

export function useHotkeyTree() {
  return useQuery({
    queryKey: TREE_KEY,
    queryFn: async (): Promise<HotkeyTreeResponse> => {
      const res = await apiClient.get<HotkeyTreeResponse>('/catalog-hotkeys');
      return res.data;
    },
  });
}

export function useFunctionKeys() {
  return useQuery({
    queryKey: FN_KEY,
    queryFn: async (): Promise<FunctionKeyVocabulary> => {
      const res = await apiClient.get<FunctionKeyVocabulary>('/catalog-hotkeys/function-keys');
      return res.data;
    },
    staleTime: 5 * 60 * 1000, // vocabulary changes are rare
  });
}

export type CreateNodeInput = {
  parentId: string | null;
  sortOrder: number;
  label: string;
  kind: CatalogHotkeyNodeKind;
  payload: string | null;
  iconName: string | null;
  /** RFL-206: bind a category to a group node (optional). */
  categoryId?: string | null;
};

export function useCreateNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateNodeInput): Promise<HotkeyNode> => {
      const res = await apiClient.post<HotkeyNode>('/catalog-hotkeys/nodes', input);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export type PatchNodeInput = Partial<Omit<CreateNodeInput, 'sortOrder'>> & {
  expectedVersion: number;
  sortOrder?: number;
};

export function useUpdateNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: { id: string; patch: PatchNodeInput }): Promise<HotkeyNode> => {
      const res = await apiClient.patch<HotkeyNode>(
        `/catalog-hotkeys/nodes/${vars.id}`,
        vars.patch,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export function useDeleteNode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<{ removed: number }> => {
      const res = await apiClient.delete<{ removed: number }>(`/catalog-hotkeys/nodes/${id}`);
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TREE_KEY }),
  });
}

export function useReorderChildren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      parentId: string | null;
      orderedChildIds: string[];
    }): Promise<{ version: number; orderedChildIds: string[] }> => {
      const res = await apiClient.patch<{ version: number; orderedChildIds: string[] }>(
        '/catalog-hotkeys/reorder',
        vars,
      );
      return res.data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: TREE_KEY }),
  });
}
